// Address Autocomplete Controller - Ideal Postcodes API
// Provides window.idealPostcodesSearchAddresses for SlimSelect address lookup.
// Uses Ideal Postcodes /addresses API: 1 request returns full addresses in one call.
// Maps to the format expected by LandRegistry, EPC, verify_address, 27tec (address, postcode, line_1, etc.).

import { Controller } from "@hotwired/stimulus"

const IDEAL_POSTCODES_BASE = "https://api.ideal-postcodes.co.uk/v1"

export default class extends Controller {
  connect() {
    const apiKey = this.element.dataset.addressAutocompleteApiKey || window.idealPostcodesApiKey
    if (!apiKey) return
    this.setupIdealPostcodesAddress(apiKey)
  }

  setupIdealPostcodesAddress(apiKey) {
    if (window.idealPostcodesSearchAddresses) return

    const formatResultToAddress = this.formatResultToAddress.bind(this)

    window.idealPostcodesSearchAddresses = (search, options = {}) => {
      if (!search || search.trim().length < 4) {
        return Promise.resolve([])
      }

      const query = encodeURIComponent(search.trim())
      const addressesUrl = `${IDEAL_POSTCODES_BASE}/addresses?query=${query}&api_key=${apiKey}`

      return fetch(addressesUrl)
        .then((res) => res.json())
        .then((body) => {
          if (body.code !== 2000 || !body.result?.hits?.length) {
            return []
          }
          const hits = body.result.hits
          return hits
            .map((hit) => formatResultToAddress(hit))
            .filter((addr) => addr?.postcode)
        })
        .catch(() => [])
    }
  }

  formatResultToAddress(result) {
    // Ideal Postcodes /addresses or UDPRN result: line_1, line_2, line_3, post_town, postcode, county, etc.
    const line1 = result.line_1 || result.address_line_1 || ""
    const line2 = result.line_2 || result.address_line_2 || ""
    const line3 = result.line_3 || result.address_line_3 || ""
    const postTown = result.post_town || result.post_town_name || ""
    const postcode = result.postcode || ""
    const county = result.county || result.traditional_county || ""

    // Build address without postcode so views that display "address, postcode" don't duplicate it
    const addressParts = [line1, line2, line3, postTown].filter(Boolean)
    const address = addressParts.join(", ") || result.summaryline || ""

    const out = {
      address,
      postcode: postcode || null,
      building_number: result.building_number || result.building_name || null,
      line_1: line1 || null,
      line_2: line2 || null,
      line_3: line3 || null,
      town_or_city: postTown || null,
      county: county || null,
      district: result.district || null,
      country: result.country || "United Kingdom"
    }
    Object.keys(out).forEach((k) => {
      if (out[k] === null || out[k] === "") delete out[k]
    })
    return out
  }
}
